import { Attachment } from "@/api/attachment";
import { Instance } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import {
  hasValidDimensions,
  isPhotoModuleEnabled,
  isSculptureModuleEnabled,
} from "@/lib/compute-field/artwork-module-helpers";
import {
  computeFieldArtworkOverviewSummary,
  computeFieldLocalisationHistoriqueArtwork,
  computeFieldRelatedAuctions,
  computeFieldRelatedAuthorsArtwork,
  computeFieldRelatedBibliographies,
  computeFieldRelatedExhibitions,
  computeFieldTirageHistory,
} from "@/lib/computeFields";
import { getDefaultAttachment } from "@/lib/image";
import { buildArtworkProvenance } from "@/lib/artwork-provenance";
import { buildSculptureEditionProvenance } from "@/lib/sculpture-provenance";
import { filterPublishedTirageInAuction } from "@/lib/tirage-auctions";
import { buildTirageProvenance } from "@/lib/tirage-provenance";
import {
  mapSculptureInAuctionsToArtwork,
  mapSculptureInBibliographiesToArtwork,
  mapSculptureInExhibitionsToArtwork,
} from "@/lib/sculpture-relation-mappers";
import {
  mapTirageInAuctionsToArtwork,
  mapTirageInBibliographiesToArtwork,
  mapTirageInExhibitionsToArtwork,
} from "@/lib/tirage-relation-mappers";
import { Artwork, ArtworkBasic, SculptureEdition } from "@/lib/types";
import {
  ComputedFields,
  MergedDisplayData,
} from "@/pages/dashboard/artwork-overview/types";
import { useMemo } from "react";

export interface UseArtworkPageDataProps {
  data: Artwork & {
    tirageData?: any;
    sculptureEditionData?: any;
    sculptureEditions?: SculptureEdition[];
  };
  attachments: Attachment[];
  instanceData: Instance | null;
  locale: SupportedLocale;
  t: (key: string, options?: any) => string;
  filterFromGeneralCR: {
    showPublicAuctions: boolean;
  };
  itemType?: "ARTWORK" | "PHOTO_MASTER" | "TIRAGE" | "SCULPTURE";
}

export interface UseArtworkPageDataResult {
  displayData: MergedDisplayData;
  mainImage: Attachment | null;
  otherImages: Attachment[];
  isTirage: boolean;
  isPhotoMaster: boolean;
  isSculptureEdition: boolean;
  isSculptureMaster: boolean;
  tirageData: any;
  sculptureEditionData: any;
  sculptureEditions: SculptureEdition[];
  isPhotoModuleEnabledValue: boolean;
  isSculptureModuleEnabledValue: boolean;
  hasValidDimensionsValue: boolean;
  computedFields: ComputedFields;
  masterInfo: ArtworkBasic | Artwork | null;
  provenanceMode: boolean;
}

function determineItemType(
  itemType?: "ARTWORK" | "PHOTO_MASTER" | "TIRAGE" | "SCULPTURE",
  tirageData?: any,
  sculptureEditionData?: any,
): { isTirage: boolean; isPhotoMaster: boolean; isSculptureEdition: boolean } {
  const isTirage = itemType === "TIRAGE" || !!tirageData;
  const isPhotoMaster = itemType === "PHOTO_MASTER";
  const isSculptureEdition =
    itemType === "SCULPTURE" || !!sculptureEditionData;

  return {
    isTirage,
    isPhotoMaster,
    isSculptureEdition,
  };
}

function mergeDisplayData(
  data: Artwork & { tirageData?: any; sculptureEditionData?: any },
  flags: { isTirage: boolean; isSculptureEdition: boolean },
  tirageData?: any,
  sculptureEditionData?: any,
): MergedDisplayData {
  if (flags.isTirage) {
    // Merge Tirage fields into Artwork structure for compatibility
    return {
      ...data,
      oeuvresDescriptionNumeroEnregistrement:
        tirageData?.tirageDescriptionNumeroEnregistrement ||
        data.oeuvresDescriptionNumeroEnregistrement,
      oeuvresDescriptionNumeroAutre:
        tirageData?.tirageDescriptionNumeroAutre ||
        data.oeuvresDescriptionNumeroAutre,
      oeuvresInformationAutreNumeroPublished:
        tirageData?.tirageDescriptionNumeroAutrePublished ??
        data.oeuvresInformationAutreNumeroPublished,
      oeuvresInformationAncienNumero:
        tirageData?.tirageDescriptionNumeroAncien ||
        data.oeuvresInformationAncienNumero,
      oeuvresInformationAncienNumeroPublished:
        tirageData?.tirageDescriptionNumeroAncienPublished ??
        data.oeuvresInformationAncienNumeroPublished,
      oeuvresDescriptionDateDeCreation:
        tirageData?.tirageDescriptionDateCreation ||
        data.oeuvresDescriptionDateDeCreation,
      oeuvresDescriptionTechnique:
        tirageData?.tirageDescriptionTechnique ||
        data.oeuvresDescriptionTechnique,
      oeuvresDescriptionSupport:
        tirageData?.tirageDescriptionSupport || data.oeuvresDescriptionSupport,
      oeuvresDescriptionDimensionsOeuvre:
        tirageData?.tirageDescriptionDimensionsWithMargins
          ? typeof tirageData.tirageDescriptionDimensionsWithMargins === "string"
            ? tirageData.tirageDescriptionDimensionsWithMargins
            : JSON.stringify(tirageData.tirageDescriptionDimensionsWithMargins)
          : tirageData?.tirageDescriptionDimensionsWithoutMargins
            ? typeof tirageData.tirageDescriptionDimensionsWithoutMargins ===
              "string"
              ? tirageData.tirageDescriptionDimensionsWithoutMargins
              : JSON.stringify(
                  tirageData.tirageDescriptionDimensionsWithoutMargins,
                )
            : data.oeuvresDescriptionDimensionsOeuvre,
      artworkInExhibitions:
        mapTirageInExhibitionsToArtwork(tirageData?.tirageInExhibition) ??
        data.artworkInExhibitions,
      artworkInAuctions:
        mapTirageInAuctionsToArtwork(tirageData?.tirageInAuction) ??
        data.artworkInAuctions,
      artworkInBibliography:
        mapTirageInBibliographiesToArtwork(tirageData?.tirageInBibliography) ??
        data.artworkInBibliography,
      artworkHistory: data.artworkHistory,
      tirageHistory: (tirageData?.tirageHistory || []) as any[],
    } as MergedDisplayData;
  }

  if (flags.isSculptureEdition) {
    // Scope history + exhibitions / bibliographies / auctions strictly to the
    // edition (no master artwork leakage). artworkHistory is cleared so the
    // HistoryAccordion only renders the sculpture edition provenance.
    const seIn = sculptureEditionData || {};
    return {
      ...data,
      oeuvresDescriptionNumeroEnregistrement:
        seIn.sculptureEditionNumeroEnregistrement ||
        data.oeuvresDescriptionNumeroEnregistrement,
      oeuvresDescriptionNumeroAutre:
        seIn.sculptureEditionAutreNumero || data.oeuvresDescriptionNumeroAutre,
      oeuvresInformationAncienNumero:
        seIn.sculptureEditionAncienNumero ||
        data.oeuvresInformationAncienNumero,
      oeuvresDescriptionDateDeCreation:
        seIn.sculptureEditionDateRealisation ||
        data.oeuvresDescriptionDateDeCreation,
      oeuvresDescriptionDateDeCreationInconnue:
        data.oeuvresDescriptionDateDeCreationInconnue,
      oeuvresDescriptionTechnique:
        seIn.sculptureEditionTechnique || data.oeuvresDescriptionTechnique,
      oeuvresDescriptionSupport:
        seIn.sculptureEditionSupport || data.oeuvresDescriptionSupport,
      oeuvresDescriptionDimensionsOeuvre: seIn.sculptureEditionDimensions
        ? typeof seIn.sculptureEditionDimensions === "string"
          ? seIn.sculptureEditionDimensions
          : JSON.stringify(seIn.sculptureEditionDimensions)
        : data.oeuvresDescriptionDimensionsOeuvre,
      oeuvresDescriptionSignatureTranscription:
        seIn.sculptureEditionSignature ||
        data.oeuvresDescriptionSignatureTranscription,
      oeuvresDescriptionInscriptionsDosMultilingual:
        seIn.sculptureEditionMarqueTampons ||
        data.oeuvresDescriptionInscriptionsDosMultilingual,
      oeuvresDescriptionInscriptionsDosPublished:
        seIn.sculptureEditionMarqueTamponsPublished ??
        data.oeuvresDescriptionInscriptionsDosPublished,
      artworkInExhibitions:
        (mapSculptureInExhibitionsToArtwork(
          seIn.sculptureEditionInExhibitions,
        ) as any) ?? [],
      artworkInAuctions:
        (mapSculptureInAuctionsToArtwork(
          seIn.sculptureEditionInAuction,
        ) as any) ?? [],
      artworkInBibliography:
        (mapSculptureInBibliographiesToArtwork(
          seIn.sculptureEditionInBibliography,
        ) as any) ?? [],
      artworkHistory: [],
      sculptureEditionHistory: (seIn.history || []) as any[],
      sculptureEditionAuctions: (seIn.sculptureEditionInAuction || []) as any[],
      sculptureEditionMouvements: (seIn.mouvements || []) as any[],
    } as MergedDisplayData;
  }

  return data as MergedDisplayData;
}

export function useArtworkPageData({
  data,
  attachments,
  instanceData,
  locale,
  t,
  filterFromGeneralCR,
  itemType,
}: UseArtworkPageDataProps): UseArtworkPageDataResult {
  // All hooks must be called before any conditional returns
  const { isTirage, isPhotoMaster, isSculptureEdition } = useMemo(
    () =>
      determineItemType(
        itemType,
        data?.tirageData,
        data?.sculptureEditionData,
      ),
    [itemType, data?.tirageData, data?.sculptureEditionData],
  );

  const tirageData = useMemo(() => data?.tirageData, [data?.tirageData]);
  const sculptureEditionData = useMemo(
    () => data?.sculptureEditionData,
    [data?.sculptureEditionData],
  );
  const sculptureEditions = useMemo<SculptureEdition[]>(
    () => {
      const editions = data?.sculptureEditions;
      return Array.isArray(editions) ? editions : [];
    },
    [data?.sculptureEditions],
  );

  const displayData = useMemo(
    () =>
      data
        ? mergeDisplayData(
            data,
            { isTirage, isSculptureEdition },
            tirageData,
            sculptureEditionData,
          )
        : ({} as MergedDisplayData),
    [data, isTirage, isSculptureEdition, tirageData, sculptureEditionData],
  );

  const isPhotoModuleEnabledValue = useMemo(
    () =>
      displayData && instanceData
        ? isPhotoModuleEnabled(displayData, instanceData, locale)
        : false,
    [displayData, instanceData, locale],
  );

  const isSculptureModuleEnabledValue = useMemo(
    () =>
      displayData && instanceData
        ? isSculptureModuleEnabled(displayData, instanceData, locale)
        : false,
    [displayData, instanceData, locale],
  );

  const isSculptureMaster =
    isSculptureModuleEnabledValue && !isSculptureEdition;

  const mainImage = useMemo(
    () =>
      getDefaultAttachment(
        attachments,
        isSculptureEdition
          ? "sculptureEdition"
          : isTirage
            ? "tirage"
            : isSculptureMaster
              ? "sculptureEdition"
              : "artwork",
      ),
    [attachments, isSculptureEdition, isTirage, isSculptureMaster],
  );

  const otherImages = useMemo(
    () =>
      attachments
        .filter((attachment) => attachment.id !== mainImage?.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [attachments, mainImage],
  );

  const hasValidDimensionsValue = useMemo(
    () => (displayData ? hasValidDimensions(displayData) : false),
    [displayData],
  );

  const masterInfo = useMemo(
    () =>
      data && isSculptureEdition
        ? ((data as Artwork | ArtworkBasic) ?? null)
        : null,
    [isSculptureEdition, data],
  );

  // Compute fields
  const provenanceMode = instanceData?.provenanceMode ?? false;

  const historyCalculated = useMemo(() => {
    if (!displayData || isTirage || isSculptureEdition) return "";
    if (provenanceMode) {
      return (
        buildArtworkProvenance({
          history: displayData.artworkHistory as any,
          auctions: displayData.artworkInAuctions as any,
          t,
        }) ?? ""
      );
    }
    return computeFieldLocalisationHistoriqueArtwork(displayData, {
      language: locale,
    });
  }, [displayData, isTirage, isSculptureEdition, locale, provenanceMode, t]);

  const tirageHistoryCalculated = useMemo(() => {
    if (!isTirage) {
      return null;
    }
    const hasHistory = (displayData?.tirageHistory?.length ?? 0) > 0;
    const publishedTirageAuctions = filterPublishedTirageInAuction(
      tirageData?.tirageInAuction,
    );
    if (provenanceMode) {
      if (!hasHistory && publishedTirageAuctions.length === 0) {
        return null;
      }
      return buildTirageProvenance({
        history: displayData.tirageHistory,
        auctions: publishedTirageAuctions,
        t,
      });
    }
    if (!hasHistory) {
      return null;
    }
    return computeFieldTirageHistory(
      { tirageHistory: displayData.tirageHistory ?? [] },
      { language: locale, translation: t, lineBreak: true },
    );
  }, [
    isTirage,
    displayData?.tirageHistory,
    locale,
    provenanceMode,
    t,
    tirageData?.tirageInAuction,
  ]);

  const sculptureEditionHistoryCalculated = useMemo<string | null>(() => {
    if (!isSculptureEdition) {
      return null;
    }
    const hasHistory =
      (displayData?.sculptureEditionHistory?.length ?? 0) > 0 ||
      (provenanceMode &&
        (displayData?.sculptureEditionAuctions?.length ?? 0) > 0);
    if (!hasHistory) return null;
    return buildSculptureEditionProvenance({
      history: displayData?.sculptureEditionHistory as any,
      auctions: provenanceMode
        ? (displayData?.sculptureEditionAuctions as any)
        : [],
      t,
    });
  }, [
    isSculptureEdition,
    displayData?.sculptureEditionHistory,
    displayData?.sculptureEditionAuctions,
    provenanceMode,
    t,
  ]);

  const exhibitionsCalculated = useMemo(
    () =>
      displayData?.artworkInExhibitions
        ? computeFieldRelatedExhibitions(
            { relatedExhbitions: displayData.artworkInExhibitions },
            { language: locale },
          )
        : "",
    [displayData?.artworkInExhibitions, locale],
  );

  const auctionCalculated = useMemo(() => {
    if (provenanceMode) return null;
    if (
      !filterFromGeneralCR.showPublicAuctions ||
      !displayData?.artworkInAuctions
    )
      return null;
    return computeFieldRelatedAuctions(
      { relatedAuctions: displayData.artworkInAuctions },
      { language: locale },
    );
  }, [
    provenanceMode,
    filterFromGeneralCR.showPublicAuctions,
    displayData?.artworkInAuctions,
    locale,
  ]);

  const bibliographyCalculated = useMemo(
    () =>
      displayData?.artworkInBibliography
        ? computeFieldRelatedBibliographies(
            { relatedBibliographies: displayData.artworkInBibliography },
            { language: locale },
          )
        : "",
    [displayData?.artworkInBibliography, locale],
  );

  const artistsCalculated = useMemo(
    () =>
      displayData
        ? computeFieldRelatedAuthorsArtwork(displayData, { language: locale })
        : "",
    [displayData, locale],
  );

  const overviewSummaryCalculated = useMemo(() => {
    if (!displayData) return "";
    const overviewInput = {
      ...(itemType === "SCULPTURE" ? (masterInfo as Artwork) : displayData),
      preposition: t("computeField.techniqueSupportPreposition"),
      doubleFace: t("computeField.doubleFace"),
      itemType: itemType ?? "ARTWORK",
    };
    return computeFieldArtworkOverviewSummary(overviewInput, {
      language: locale,
      translation: t,
      isPhotoModuleEnabled: isPhotoModuleEnabledValue,
      hasValidDimensions: hasValidDimensionsValue,
    });
  }, [
    displayData,
    locale,
    t,
    itemType,
    isPhotoModuleEnabledValue,
    hasValidDimensionsValue,
    masterInfo,
  ]);

  const computedFields: ComputedFields = useMemo(
    () => ({
      historyCalculated,
      tirageHistoryCalculated,
      sculptureEditionHistoryCalculated,
      exhibitionsCalculated,
      auctionCalculated,
      bibliographyCalculated,
      artistsCalculated,
      overviewSummaryCalculated,
    }),
    [
      historyCalculated,
      tirageHistoryCalculated,
      sculptureEditionHistoryCalculated,
      exhibitionsCalculated,
      auctionCalculated,
      bibliographyCalculated,
      artistsCalculated,
      overviewSummaryCalculated,
    ],
  );

  // Now we can safely return, handling the null data case
  if (!data) {
    return {
      displayData: {} as MergedDisplayData,
      mainImage: (mainImage ?? null) as Attachment | null,
      otherImages: (otherImages ?? []) as Attachment[],
      isTirage: false,
      isPhotoMaster: false,
      isSculptureEdition: false,
      isSculptureMaster: false,
      tirageData: undefined,
      sculptureEditionData: undefined,
      sculptureEditions: [],
      isPhotoModuleEnabledValue: false,
      isSculptureModuleEnabledValue: false,
      hasValidDimensionsValue: false,
      masterInfo: null,
      provenanceMode: false,
      computedFields: {
        historyCalculated: "",
        tirageHistoryCalculated: null,
        sculptureEditionHistoryCalculated: null,
        exhibitionsCalculated: "",
        auctionCalculated: null,
        bibliographyCalculated: "",
        artistsCalculated: "",
        overviewSummaryCalculated: "",
      },
    };
  }

  return {
    displayData,
    mainImage: mainImage as Attachment | null,
    otherImages: otherImages as Attachment[],
    isTirage,
    isPhotoMaster,
    isSculptureEdition,
    isSculptureMaster,
    tirageData,
    sculptureEditionData,
    sculptureEditions,
    isPhotoModuleEnabledValue,
    isSculptureModuleEnabledValue,
    hasValidDimensionsValue,
    masterInfo,
    computedFields,
    provenanceMode,
  };
}
